import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useRestaurant } from '@/hooks/useRestaurant';
import { useFilteredBranches } from '@/hooks/useFilteredBranches';
import { useSubscription } from '@/hooks/useSubscription';
import { useTranslation } from 'react-i18next';
import { FeatureGuard } from '@/components/subscription/FeatureGuard';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  FileText,
  Download,
  Plus,
  Calculator,
  PieChart,
  BarChart3,
  Receipt,
  RefreshCw,
  Scale,
  Info,
  AlertTriangle,
  Lock
} from 'lucide-react';
import {
  generateProfitLossReport,
  generateCashFlowReport,
  generateExpenseSummary,
  generateTaxReport,
  fetchExpenses,
  fetchFinancialAccounts,
  ProfitLossReport,
  CashFlowReport,
  ExpenseSummary,
  Expense,
  FinancialAccount
} from '@/services/financialService';
import { AddFinancialAccountDialog } from './AddFinancialAccountDialog';
import { EditFinancialAccountDialog } from './EditFinancialAccountDialog';
import { ManualTransactionDialog } from './ManualTransactionDialog';
import { AccountTransactionHistory } from './AccountTransactionHistory';
import { SetOpeningBalancesDialog } from './SetOpeningBalancesDialog';

export function FinancialDashboard() {
  const { user } = useAuth();
  const { restaurant } = useRestaurant();
  const { branches } = useFilteredBranches();
  const { subscription } = useSubscription();
  const { t } = useTranslation();
  const { toast } = useToast();
  
  // Finance tier access levels:
  // basic: Daily Sales only (shown in Orders page, limited access here)
  // advanced: P&L reports, Expense Tracking
  // enterprise: Full Balance Sheet, Cash Flow, Tax Reports, Financial Accounts
  const financeTier = subscription?.features?.finance_tier as string || 'basic';
  const hasAdvancedFinance = financeTier === 'advanced' || financeTier === 'enterprise';
  const hasEnterpriseFinance = financeTier === 'enterprise';
  
  const [loading, setLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<string>('all');
  const [startDate, setStartDate] = useState<Date>(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [endDate, setEndDate] = useState<Date>(new Date());
  
  const [profitLossReport, setProfitLossReport] = useState<ProfitLossReport | null>(null);
  const [cashFlowReport, setCashFlowReport] = useState<CashFlowReport | null>(null);
  const [expenseSummary, setExpenseSummary] = useState<ExpenseSummary[]>([]);
  const [recentExpenses, setRecentExpenses] = useState<Expense[]>([]);
  const [financialAccounts, setFinancialAccounts] = useState<FinancialAccount[]>([]);
  
  // Dialog states
  const [addAccountDialogOpen, setAddAccountDialogOpen] = useState(false);
  const [editAccountDialogOpen, setEditAccountDialogOpen] = useState(false);
  const [manualTransactionDialogOpen, setManualTransactionDialogOpen] = useState(false);
  const [openingBalancesDialogOpen, setOpeningBalancesDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<FinancialAccount | null>(null);
  const [viewTransactionsAccountId, setViewTransactionsAccountId] = useState<string | null>(null);

  const loadFinancialData = useCallback(async () => {
    try {
      setLoading(true);
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      const branchId = selectedBranch === 'all' ? undefined : selectedBranch;

      const [plReport, cfReport, expSummary, expenses, accounts] = await Promise.all([
        generateProfitLossReport(startDateStr, endDateStr, branchId),
        generateCashFlowReport(startDateStr, endDateStr, branchId),
        generateExpenseSummary(startDateStr, endDateStr, branchId),
        fetchExpenses({ 
          branchId,
          startDate: startDateStr,
          endDate: endDateStr,
          limit: 10 
        }),
        fetchFinancialAccounts()
      ]);

      setProfitLossReport(plReport);
      setCashFlowReport(cfReport);
      setExpenseSummary(expSummary);
      setRecentExpenses(expenses);
      setFinancialAccounts(accounts);
    } catch (error) {
      console.error('Error loading financial data:', error);
      toast({
        title: "Error",
        description: "Failed to load financial data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, startDate, endDate, toast]);

  useEffect(() => {
    loadFinancialData();
  }, [loadFinancialData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ar-EG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount) + ' EGP';
  };

  const formatDate = (dateStr: string | Date) => {
    if (dateStr instanceof Date) {
      return dateStr.toLocaleDateString();
    }
    return new Date(dateStr).toLocaleDateString();
  };

  const getPercentageIcon = (percentage: number) => {
    if (percentage > 0) return <TrendingUp className="h-4 w-4 text-green-600" />;
    if (percentage < 0) return <TrendingDown className="h-4 w-4 text-red-600" />;
    return null;
  };

  // Group financial accounts by type
  const groupedAccounts = financialAccounts.reduce((acc, account) => {
    if (!acc[account.account_type]) {
      acc[account.account_type] = [];
    }
    acc[account.account_type].push(account);
    return acc;
  }, {} as Record<string, FinancialAccount[]>);

  // Calculate totals for balance sheet
  const assetTotal = groupedAccounts.asset?.reduce((sum, acc) => sum + acc.current_balance, 0) || 0;
  const liabilityTotal = groupedAccounts.liability?.reduce((sum, acc) => sum + acc.current_balance, 0) || 0;
  const equityTotal = groupedAccounts.equity?.reduce((sum, acc) => sum + acc.current_balance, 0) || 0;

  if (loading && !profitLossReport) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">Loading financial data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header with filters */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg md:text-2xl font-bold">Financial Reports</h1>
            <p className="text-xs md:text-sm text-gray-600 hidden sm:block">Track your restaurant's financial performance</p>
          </div>
          <Button onClick={loadFinancialData} variant="outline" size="sm" className="h-8 w-8 md:h-9 md:w-auto md:px-3 p-0">
            <RefreshCw className="h-4 w-4" />
            <span className="hidden md:inline ml-2">Refresh</span>
          </Button>
        </div>
        
        {/* Filters - horizontal scroll on mobile */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2 md:mx-0 md:px-0 md:overflow-visible scrollbar-hide">
          <Select value={selectedBranch} onValueChange={setSelectedBranch}>
            <SelectTrigger className="w-32 md:w-48 flex-shrink-0 h-8 md:h-9 text-xs md:text-sm">
              <SelectValue placeholder="Branch" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map((branch) => (
                <SelectItem key={branch.id} value={branch.id}>
                  {branch.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DatePicker
            selected={startDate}
            onSelect={(date) => date && setStartDate(date)}
            placeholderText="Start"
          />

          <DatePicker
            selected={endDate}
            onSelect={(date) => date && setEndDate(date)}
            placeholderText="End"
          />
        </div>
      </div>

      {/* Key Metrics Cards */}
      {profitLossReport && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4">
          <Card>
            <CardContent className="p-2 md:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs md:text-sm text-gray-600">Revenue</p>
                  <p className="text-sm md:text-2xl font-bold truncate">{formatCurrency(profitLossReport.revenue.total_revenue)}</p>
                </div>
                <DollarSign className="h-5 w-5 md:h-8 md:w-8 text-green-600 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-2 md:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs md:text-sm text-gray-600">Gross Profit</p>
                  <p className="text-sm md:text-2xl font-bold truncate">{formatCurrency(profitLossReport.gross_profit.amount)}</p>
                  <p className="text-xs text-gray-500 hidden md:block">{profitLossReport.gross_profit.margin_percentage.toFixed(1)}% margin</p>
                </div>
                <TrendingUp className="h-5 w-5 md:h-8 md:w-8 text-blue-600 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-2 md:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs md:text-sm text-gray-600">Net Profit</p>
                  <p className={`text-sm md:text-2xl font-bold truncate ${profitLossReport.net_profit.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(profitLossReport.net_profit.amount)}
                  </p>
                  <p className="text-xs text-gray-500 hidden md:block">{profitLossReport.net_profit.margin_percentage.toFixed(1)}% margin</p>
                </div>
                <div className="flex-shrink-0">{getPercentageIcon(profitLossReport.net_profit.amount)}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-2 md:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-xs md:text-sm text-gray-600">Expenses</p>
                  <p className="text-sm md:text-2xl font-bold truncate">{formatCurrency(profitLossReport.operating_expenses.total_operating_expenses + profitLossReport.cost_of_goods_sold.total_cogs)}</p>
                  <p className="text-xs text-gray-500 hidden md:block">
                    {profitLossReport.revenue.total_revenue > 0 ? (((profitLossReport.operating_expenses.total_operating_expenses + profitLossReport.cost_of_goods_sold.total_cogs) / profitLossReport.revenue.total_revenue) * 100).toFixed(1) : 0}% of revenue
                  </p>
                </div>
                <Receipt className="h-5 w-5 md:h-8 md:w-8 text-purple-600 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Detailed Reports */}
      <Tabs defaultValue={hasAdvancedFinance ? "profit-loss" : "overview"} className="space-y-3 md:space-y-4">
        <div className="overflow-x-auto -mx-2 px-2 md:mx-0 md:px-0 scrollbar-hide">
          <TabsList className="inline-flex w-auto min-w-full md:w-auto md:min-w-0">
            {/* Basic tier only gets overview */}
            {!hasAdvancedFinance && (
              <TabsTrigger value="overview" className="text-xs md:text-sm px-2 md:px-3">
                <BarChart3 className="h-4 w-4 md:mr-1" />
                <span className="hidden md:inline">{t('finance.overview', 'Overview')}</span>
              </TabsTrigger>
            )}
            
            {/* Advanced tier: P&L and Expenses */}
            {hasAdvancedFinance && (
              <>
                <TabsTrigger value="profit-loss" className="text-xs md:text-sm px-2 md:px-3">
                  <BarChart3 className="h-4 w-4 md:mr-1" />
                  <span className="hidden md:inline">{t('finance.profit_loss', 'Profit & Loss')}</span>
                  <span className="md:hidden">P&L</span>
                </TabsTrigger>
                <TabsTrigger value="expenses" className="text-xs md:text-sm px-2 md:px-3">
                  <Receipt className="h-4 w-4 md:mr-1" />
                  <span className="hidden md:inline">{t('finance.expenses', 'Expenses')}</span>
                  <span className="md:hidden">Exp</span>
                </TabsTrigger>
              </>
            )}
            
            {/* Enterprise tier: Cash Flow and Balance Sheet */}
            {hasEnterpriseFinance ? (
              <>
                <TabsTrigger value="cash-flow" className="text-xs md:text-sm px-2 md:px-3">
                  <Calculator className="h-4 w-4 md:mr-1" />
                  <span className="hidden md:inline">{t('finance.cash_flow', 'Cash Flow')}</span>
                  <span className="md:hidden">Cash</span>
                </TabsTrigger>
                <TabsTrigger value="balance-sheet" className="text-xs md:text-sm px-2 md:px-3">
                  <Scale className="h-4 w-4 md:mr-1" />
                  <span className="hidden md:inline">{t('finance.balance_sheet', 'Balance Sheet')}</span>
                  <span className="md:hidden">Balance</span>
                </TabsTrigger>
              </>
            ) : (
              <>
                <TabsTrigger value="cash-flow" disabled className="opacity-50 text-xs md:text-sm px-2 md:px-3">
                  <Lock className="h-3 w-3 mr-1" />
                  <span className="hidden md:inline">{t('finance.cash_flow', 'Cash Flow')}</span>
                  <span className="md:hidden">Cash</span>
                </TabsTrigger>
                <TabsTrigger value="balance-sheet" disabled className="opacity-50 text-xs md:text-sm px-2 md:px-3">
                  <Lock className="h-3 w-3 mr-1" />
                  <span className="hidden md:inline">{t('finance.balance_sheet', 'Balance Sheet')}</span>
                  <span className="md:hidden">Balance</span>
                </TabsTrigger>
              </>
            )}
          </TabsList>
        </div>

        {/* Basic tier overview - upgrade prompt */}
        {!hasAdvancedFinance && (
          <TabsContent value="overview">
            <Card>
              <CardContent className="p-8 text-center">
                <Lock className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold mb-2">{t('finance.upgrade_required_title', 'Advanced Financial Reports')}</h3>
                <p className="text-gray-600 mb-4">
                  {t('finance.upgrade_required_desc', 'Upgrade to Pro or Enterprise to access detailed financial reports including Profit & Loss statements, expense tracking, cash flow analysis, and balance sheets.')}
                </p>
                <Alert className="bg-blue-50 border-blue-200 mb-4">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    {t('finance.basic_feature_hint', 'With your current plan, you can view Daily Sales Reports in the Orders page.')}
                  </AlertDescription>
                </Alert>
                <Button onClick={() => window.location.href = '/subscription'}>
                  {t('common.upgrade_now', 'Upgrade Now')}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="profit-loss">
          {profitLossReport && (
            <Card>
              <CardHeader className="py-3 md:py-6 px-3 md:px-6">
                <CardTitle className="flex items-center gap-2 text-base md:text-xl">
                  <BarChart3 className="h-4 w-4 md:h-5 md:w-5" />
                  Profit & Loss Statement
                </CardTitle>
                <p className="text-xs md:text-sm text-gray-600">
                  {formatDate(profitLossReport.period.start_date)} - {formatDate(profitLossReport.period.end_date)}
                </p>
              </CardHeader>
              <CardContent className="space-y-4 md:space-y-6 px-3 md:px-6">
                {/* Revenue Section */}
                <div>
                  <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-3">Revenue</h3>
                  <div className="space-y-1.5 md:space-y-2 text-sm md:text-base">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Dine-in</span>
                      <span className="font-medium">{formatCurrency(profitLossReport.revenue.dine_in_revenue)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Pickup</span>
                      <span className="font-medium">{formatCurrency(profitLossReport.revenue.pickup_revenue)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Delivery</span>
                      <span className="font-medium">{formatCurrency(profitLossReport.revenue.delivery_revenue)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Service Charges</span>
                      <span className="font-medium">{formatCurrency(profitLossReport.revenue.total_service_charge)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-1.5 md:pt-2">
                      <span className="font-semibold">Gross Revenue</span>
                      <span className="font-semibold">{formatCurrency(profitLossReport.revenue.total_revenue)}</span>
                    </div>
                    <div className="flex justify-between text-xs md:text-sm text-gray-600">
                      <span>Less: Taxes</span>
                      <span>({formatCurrency(profitLossReport.revenue.total_tax)})</span>
                    </div>
                    <div className="flex justify-between font-medium bg-blue-50 p-1.5 md:p-2 rounded text-sm md:text-base">
                      <span>Net Revenue</span>
                      <span>{formatCurrency(profitLossReport.revenue.net_revenue)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 md:gap-4 mt-2 md:mt-3 text-xs md:text-sm">
                      <div className="bg-gray-50 p-2 rounded">
                        <p className="text-gray-600">Orders</p>
                        <p className="font-semibold">{profitLossReport.revenue.total_orders}</p>
                      </div>
                      <div className="bg-gray-50 p-2 rounded">
                        <p className="text-gray-600">Avg Order</p>
                        <p className="font-semibold">{formatCurrency(profitLossReport.revenue.average_order_value)}</p>
                      </div>
                    </div>
                    {profitLossReport.revenue.average_order_value_by_type && (
                      <div className="mt-2 text-xs bg-gray-50 p-2 rounded space-y-1">
                        <div className="grid grid-cols-3 gap-1 text-center">
                          <div>
                            <span className="block text-gray-500">Dine-in</span>
                            <span className="font-medium">{formatCurrency(profitLossReport.revenue.average_order_value_by_type.dine_in)}</span>
                          </div>
                          <div>
                            <span className="block text-gray-500">Delivery</span>
                            <span className="font-medium">{formatCurrency(profitLossReport.revenue.average_order_value_by_type.delivery)}</span>
                          </div>
                          <div>
                            <span className="block text-gray-500">Pickup</span>
                            <span className="font-medium">{formatCurrency(profitLossReport.revenue.average_order_value_by_type.pickup)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* COGS Section */}
                <div className="border-t pt-3 md:pt-4">
                  <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-3\">Cost of Goods Sold</h3>
                  
                  {/* Calculation Method Indicator */}
                  {profitLossReport.cogs_calculation_method && (
                    <Alert className="mb-2 md:mb-3 bg-blue-50 border-blue-200\">
                      <Info className="h-3 w-3 md:h-4 md:w-4 text-blue-600\" />
                      <AlertDescription className="text-xs md:text-sm\">
                        <div className="space-y-0.5 md:space-y-1\">
                          <div className="font-medium text-blue-900\">
                            📊 {
                              profitLossReport.cogs_calculation_method === 'inventory' ? 'Inventory' :
                              profitLossReport.cogs_calculation_method === 'recipe' ? 'Recipe-Based' :
                              profitLossReport.cogs_calculation_method === 'purchases' ? 'Purchases' :
                              'N/A'
                            }
                          </div>
                          <div className="hidden md:block space-y-1\">
                            {profitLossReport.data_sources?.beginning_inventory_date && (
                              <div className="text-blue-700\">
                                📅 Begin: {new Date(profitLossReport.data_sources.beginning_inventory_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </div>
                            )}
                            {profitLossReport.data_sources?.ending_inventory_date && (
                              <div className="text-blue-700\">
                                📅 End: {new Date(profitLossReport.data_sources.ending_inventory_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </div>
                            )}
                          </div>
                          {!profitLossReport.data_sources?.has_beginning_inventory && profitLossReport.cogs_calculation_method === 'recipe' && (
                            <div className="text-yellow-800 flex items-start gap-1 mt-1 text-xs\">
                              <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0\" />
                              <span className="hidden md:inline\">No beginning inventory found - using recipe-based COGS</span>
                              <span className="md:hidden\">Recipe-based COGS</span>
                            </div>
                          )}
                          {profitLossReport.data_sources?.waste_records_count !== undefined && (
                            <div className="text-blue-700 text-xs mt-1 hidden md:block\">
                              🗑️ Waste: {profitLossReport.data_sources.waste_records_count}
                            </div>
                          )}
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Physical Count Reminder */}
                  {profitLossReport.data_sources?.ending_inventory_date && (() => {
                    const daysSinceLastCount = Math.floor(
                      (new Date().getTime() - new Date(profitLossReport.data_sources.ending_inventory_date).getTime()) / (1000 * 60 * 60 * 24)
                    );
                    return daysSinceLastCount > 30 ? (
                      <Alert className="mb-3 bg-yellow-50 border-yellow-300">
                        <AlertTriangle className="h-4 w-4 text-yellow-600" />
                        <AlertDescription className="text-sm text-yellow-900">
                          ⚠️ It's been {daysSinceLastCount} days since your last physical count. 
                          Regular counts improve COGS accuracy. Visit the Inventory page to conduct a count.
                        </AlertDescription>
                      </Alert>
                    ) : null;
                  })()}

                  <div className="space-y-1.5 md:space-y-2 text-sm">
                    {profitLossReport.cost_of_goods_sold.beginning_inventory > 0 && (
                      <>
                        <div className="flex justify-between text-xs md:text-sm">
                          <span className="text-gray-600">Beginning Inv.</span>
                          <span>{formatCurrency(profitLossReport.cost_of_goods_sold.beginning_inventory)}</span>
                        </div>
                        <div className="flex justify-between text-xs md:text-sm">
                          <span className="text-gray-600">+ Purchases</span>
                          <span>{formatCurrency(profitLossReport.cost_of_goods_sold.purchases)}</span>
                        </div>
                        <div className="flex justify-between text-xs md:text-sm">
                          <span className="text-gray-600">- Ending Inv.</span>
                          <span>({formatCurrency(profitLossReport.cost_of_goods_sold.ending_inventory)})</span>
                        </div>
                        <div className="flex justify-between border-t pt-1">
                          <span className="font-medium text-sm">Food Cost</span>
                          <span className="font-medium text-sm">{formatCurrency(profitLossReport.cost_of_goods_sold.food_cost)}</span>
                        </div>
                      </>
                    )}
                    {profitLossReport.cost_of_goods_sold.waste_amount > 0 && (
                      <div className="flex justify-between text-xs md:text-sm">
                        <span className="text-gray-600">+ Waste</span>
                        <span className="text-red-600">{formatCurrency(profitLossReport.cost_of_goods_sold.waste_amount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t pt-1.5 md:pt-2">
                      <span className="font-semibold text-sm md:text-base">Total COGS</span>
                      <span className="font-semibold text-sm md:text-base text-orange-600">{formatCurrency(profitLossReport.cost_of_goods_sold.total_cogs)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                      <div className="bg-orange-50 p-1.5 md:p-2 rounded text-center">
                        <p className="text-gray-500">COGS %</p>
                        <p className="font-semibold text-orange-700">{profitLossReport.cost_of_goods_sold.cogs_percentage.toFixed(1)}%</p>
                      </div>
                      {profitLossReport.cost_of_goods_sold.waste_percentage > 0 && (
                        <div className="bg-red-50 p-1.5 md:p-2 rounded text-center">
                          <p className="text-gray-500">Waste %</p>
                          <p className="font-semibold text-red-700">{profitLossReport.cost_of_goods_sold.waste_percentage.toFixed(1)}%</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Gross Profit */}
                <div className="bg-green-50 p-2 md:p-4 rounded-lg border border-green-200">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-sm md:text-base">Gross Profit</span>
                    <span className="font-semibold text-sm md:text-base text-green-600">{formatCurrency(profitLossReport.gross_profit.amount)}</span>
                  </div>
                  <div className="text-xs md:text-sm text-gray-600 mt-0.5 md:mt-1">
                    Margin: {profitLossReport.gross_profit.margin_percentage.toFixed(1)}%
                  </div>
                </div>

                {/* Operating Expenses Section */}
                <div className="border-t pt-3 md:pt-4">
                  <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-3">Operating Expenses</h3>
                  <div className="space-y-1.5 md:space-y-2 text-sm">
                    {profitLossReport.operating_expenses.payroll > 0 && (
                      <div className="flex justify-between text-xs md:text-sm">
                        <span className="text-gray-600">Payroll</span>
                        <span className="font-medium">{formatCurrency(profitLossReport.operating_expenses.payroll)}</span>
                      </div>
                    )}
                    {profitLossReport.operating_expenses.operating_expenses > 0 && (
                      <div className="flex justify-between">
                        <span>Other Operating Expenses</span>
                        <span className="font-medium">{formatCurrency(profitLossReport.operating_expenses.operating_expenses)}</span>
                      </div>
                    )}
                    {profitLossReport.operating_expenses.administrative_expenses > 0 && (
                      <div className="flex justify-between text-xs md:text-sm">
                        <span className="text-gray-600">Admin</span>
                        <span className="font-medium">{formatCurrency(profitLossReport.operating_expenses.administrative_expenses)}</span>
                      </div>
                    )}
                    {profitLossReport.operating_expenses.other_expenses > 0 && (
                      <div className="flex justify-between text-xs md:text-sm">
                        <span className="text-gray-600">Other</span>
                        <span className="font-medium">{formatCurrency(profitLossReport.operating_expenses.other_expenses)}</span>
                      </div>
                    )}
                    <div className="flex justify-between border-t pt-1.5 md:pt-2">
                      <span className="font-semibold text-sm md:text-base">Total OpEx</span>
                      <span className="font-semibold text-sm md:text-base">{formatCurrency(profitLossReport.operating_expenses.total_operating_expenses)}</span>
                    </div>
                    
                    {/* Expense Breakdown - hide on mobile */}
                    {profitLossReport.operating_expenses.expense_breakdown && profitLossReport.operating_expenses.expense_breakdown.length > 0 && (
                      <div className="hidden md:block mt-3 bg-gray-50 p-3 rounded text-sm">
                        <p className="font-medium mb-2">Expense Breakdown:</p>
                        {profitLossReport.operating_expenses.expense_breakdown.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-xs mb-1">
                            <span>{item.category}</span>
                            <span className="font-medium">{item.percentage.toFixed(1)}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Labor & Prime Cost Metrics */}
                {profitLossReport.labor_metrics && (
                  <div className="border-t pt-3 md:pt-4">
                    <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-3">Labor Metrics</h3>
                    <div className="grid grid-cols-3 gap-2 md:gap-3 text-xs md:text-sm">
                      <div className="bg-purple-50 p-2 md:p-3 rounded text-center">
                        <p className="text-xs text-gray-600">Labor %</p>
                        <p className="font-bold text-purple-700">{profitLossReport.labor_metrics.labor_cost_percentage.toFixed(1)}%</p>
                      </div>
                      <div className="bg-blue-50 p-2 md:p-3 rounded text-center">
                        <p className="text-xs text-gray-600">Cost/Order</p>
                        <p className="font-bold text-blue-700 text-xs md:text-sm">{formatCurrency(profitLossReport.labor_metrics.cost_per_order)}</p>
                      </div>
                      <div className={`p-2 md:p-3 rounded text-center ${profitLossReport.prime_cost.percentage <= 60 ? 'bg-green-50' : 'bg-yellow-50'}`}>
                        <p className="text-xs text-gray-600">Prime %</p>
                        <p className={`font-bold ${profitLossReport.prime_cost.percentage <= 60 ? 'text-green-700' : 'text-yellow-700'}`}>
                          {profitLossReport.prime_cost.percentage.toFixed(1)}%
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5 md:mt-2 hidden md:block">
                      Prime Cost = COGS + Labor ({formatCurrency(profitLossReport.prime_cost.amount)})
                      {profitLossReport.prime_cost.percentage <= 60 ? ' ✓ Target <60%' : ' ⚠️ Target <60%'}
                    </p>
                  </div>
                )}

                {/* Net Profit Section */}
                <div className={`p-2 md:p-4 rounded-lg border-2 ${profitLossReport.net_profit.amount >= 0 ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-sm md:text-lg">Net Profit</span>
                    <span className={`font-semibold text-sm md:text-lg ${profitLossReport.net_profit.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(profitLossReport.net_profit.amount)}
                    </span>
                  </div>
                  <div className="text-xs md:text-sm text-gray-600 mt-0.5 md:mt-1">
                    Margin: {profitLossReport.net_profit.margin_percentage.toFixed(1)}%
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="cash-flow">
          <FeatureGuard 
            feature="finance_tier" 
            requiredTier="enterprise" 
            showBanner
            fallback={
              <Card>
                <CardContent className="p-8 text-center">
                  <Lock className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">{t('finance.enterprise_feature', 'Enterprise Feature')}</h3>
                  <p className="text-gray-600 mb-4">
                    {t('finance.cash_flow_upgrade', 'Cash Flow analysis is available on the Enterprise plan. Upgrade to track detailed cash movements.')}
                  </p>
                  <Button onClick={() => window.location.href = '/subscription'}>
                    {t('common.upgrade_now', 'Upgrade Now')}
                  </Button>
                </CardContent>
              </Card>
            }
          >
          {cashFlowReport && (
            <Card>
              <CardHeader className="py-3 md:py-6 px-3 md:px-6">
                <CardTitle className="flex items-center gap-2 text-base md:text-xl">
                  <Calculator className="h-4 w-4 md:h-5 md:w-5" />
                  Cash Flow Statement
                </CardTitle>
                <p className="text-xs md:text-sm text-gray-600">
                  {formatDate(cashFlowReport.period.start_date)} - {formatDate(cashFlowReport.period.end_date)}
                </p>
              </CardHeader>
              <CardContent className="space-y-3 md:space-y-6 px-3 md:px-6">
                {/* Operating Activities */}
                <div>
                  <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-3">Operating Activities</h3>
                  <div className="space-y-1.5 md:space-y-2 text-sm">
                    <div className="flex justify-between text-xs md:text-sm">
                      <span className="text-gray-600">Sales</span>
                      <span className="font-medium text-green-600">{formatCurrency(cashFlowReport.operating_activities.cash_from_sales)}</span>
                    </div>
                    <div className="flex justify-between text-xs md:text-sm">
                      <span className="text-gray-600">Suppliers</span>
                      <span className="font-medium text-red-600">({formatCurrency(cashFlowReport.operating_activities.cash_to_suppliers)})</span>
                    </div>
                    <div className="flex justify-between text-xs md:text-sm">
                      <span className="text-gray-600">Operating Exp.</span>
                      <span className="font-medium text-red-600">({formatCurrency(cashFlowReport.operating_activities.cash_for_operating_expenses)})</span>
                    </div>
                    <div className="flex justify-between border-t pt-1.5 md:pt-2">
                      <span className="font-semibold text-sm">Net Operating</span>
                      <span className={`font-semibold text-sm ${cashFlowReport.operating_activities.net_operating_cash_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(cashFlowReport.operating_activities.net_operating_cash_flow)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Investing Activities */}
                <div>
                  <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-3">Investing Activities</h3>
                  <div className="space-y-1.5 md:space-y-2 text-sm">
                    <div className="flex justify-between text-xs md:text-sm">
                      <span className="text-gray-600">Equipment</span>
                      <span className="font-medium text-red-600">({formatCurrency(cashFlowReport.investing_activities.equipment_purchases)})</span>
                    </div>
                    <div className="flex justify-between border-t pt-1.5 md:pt-2">
                      <span className="font-semibold text-sm">Net Investing</span>
                      <span className={`font-semibold text-sm ${cashFlowReport.investing_activities.net_investing_cash_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(cashFlowReport.investing_activities.net_investing_cash_flow)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Net Cash Flow */}
                <div className={`p-2 md:p-4 rounded-lg ${cashFlowReport.net_cash_flow >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-sm md:text-base">Net Cash Flow</span>
                    <span className={`font-semibold text-sm md:text-base ${cashFlowReport.net_cash_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(cashFlowReport.net_cash_flow)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          </FeatureGuard>
        </TabsContent>

        <TabsContent value="expenses">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6">
            {/* Expense Summary */}
            <Card>
              <CardHeader className="py-3 md:py-6 px-3 md:px-6">
                <CardTitle className="flex items-center gap-2 text-base md:text-xl">
                  <PieChart className="h-4 w-4 md:h-5 md:w-5" />
                  Expense Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 md:px-6">
                {expenseSummary.length === 0 ? (
                  <div className="text-center py-6 md:py-8 text-gray-500 text-sm">
                    No expenses found for this period
                  </div>
                ) : (
                  <div className="space-y-3 md:space-y-4">
                    {expenseSummary.map((category, index) => (
                      <div key={index} className="space-y-1 md:space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs md:text-sm font-medium truncate flex-1 mr-2">{category.category}</span>
                          <span className="text-xs md:text-sm font-semibold flex-shrink-0">{formatCurrency(category.total_amount)}</span>
                        </div>
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>{category.expense_count} exp.</span>
                          <span>{category.percentage_of_total.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5 md:h-2">
                          <div 
                            className="bg-blue-600 h-1.5 md:h-2 rounded-full" 
                            style={{ width: `${category.percentage_of_total}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Expenses */}
            <Card>
              <CardHeader className="py-3 md:py-6 px-3 md:px-6">
                <CardTitle className="flex items-center gap-2 text-base md:text-xl">
                  <FileText className="h-4 w-4 md:h-5 md:w-5" />
                  Recent Expenses
                </CardTitle>
              </CardHeader>
              <CardContent className="px-3 md:px-6">
                {recentExpenses.length === 0 ? (
                  <div className="text-center py-6 md:py-8 text-gray-500 text-sm">
                    No recent expenses found
                  </div>
                ) : (
                  <div className="space-y-2 md:space-y-4">
                    {recentExpenses.map((expense) => (
                      <div key={expense.id} className="flex justify-between items-start gap-2 p-2 md:p-3 border rounded-lg">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{expense.description}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {expense.category?.name} • {formatDate(expense.expense_date)}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-semibold text-sm">{formatCurrency(expense.amount)}</p>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full inline-block ${
                            expense.status === 'paid' ? 'bg-green-100 text-green-800' :
                            expense.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {expense.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="balance-sheet">
          <FeatureGuard 
            feature="finance_tier" 
            requiredTier="enterprise" 
            showBanner
            fallback={
              <Card>
                <CardContent className="p-4 md:p-8 text-center">
                  <Lock className="h-10 w-10 md:h-12 md:w-12 mx-auto text-gray-400 mb-3 md:mb-4" />
                  <h3 className="text-lg md:text-xl font-semibold mb-2">{t('finance.enterprise_feature', 'Enterprise Feature')}</h3>
                  <p className="text-sm md:text-base text-gray-600 mb-4">
                    {t('finance.balance_sheet_upgrade', 'Balance Sheet and Financial Accounts management is available on the Enterprise plan. Upgrade to track assets, liabilities, and equity.')}
                  </p>
                  <Button onClick={() => window.location.href = '/subscription'} size="sm">
                    {t('common.upgrade_now', 'Upgrade Now')}
                  </Button>
                </CardContent>
              </Card>
            }
          >
          {viewTransactionsAccountId ? (
            <div className="space-y-3 md:space-y-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setViewTransactionsAccountId(null)}
              >
                ← Back
              </Button>
              <AccountTransactionHistory 
                accountId={viewTransactionsAccountId}
                accountName={financialAccounts.find(a => a.id === viewTransactionsAccountId)?.account_name || ''}
              />
            </div>
          ) : (
            <>
              <div className="mb-3 md:mb-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
                <h3 className="text-base md:text-lg font-semibold">Financial Accounts</h3>
                <div className="flex gap-2 w-full md:w-auto">
                  <Button variant="outline" size="sm" onClick={() => setOpeningBalancesDialogOpen(true)} className="flex-1 md:flex-initial text-xs md:text-sm h-8">
                    <Calculator className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                    <span className="hidden md:inline">Set Opening Balances</span>
                    <span className="md:hidden">Balances</span>
                  </Button>
                  <Button size="sm" onClick={() => setAddAccountDialogOpen(true)} className="flex-1 md:flex-initial text-xs md:text-sm h-8">
                    <Plus className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                    <span className="hidden md:inline">Add Account</span>
                    <span className="md:hidden">Add</span>
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
            {/* Assets */}
            <Card>
              <CardHeader className="py-2 md:py-4 px-3 md:px-6">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                  <DollarSign className="h-4 w-4" />
                  Assets
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 md:space-y-3 px-3 md:px-6">
                {groupedAccounts.asset && groupedAccounts.asset.length > 0 ? (
                  <>
                    {groupedAccounts.asset.map((account) => (
                      <div key={account.id} className="p-2 border rounded hover:bg-gray-50">
                        <div className="flex justify-between items-start mb-1.5 md:mb-2">
                          <p className="font-medium text-xs md:text-sm truncate flex-1 mr-2">{account.account_name}</p>
                          <span className="font-semibold text-xs md:text-sm flex-shrink-0">{formatCurrency(account.current_balance)}</span>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 md:h-7 text-xs px-2"
                            onClick={() => setViewTransactionsAccountId(account.id)}
                          >
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 md:h-7 text-xs px-2"
                            onClick={() => {
                              setSelectedAccount(account);
                              setManualTransactionDialogOpen(true);
                            }}
                          >
                            +Txn
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 md:h-7 text-xs px-2"
                            onClick={() => {
                              setSelectedAccount(account);
                              setEditAccountDialogOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 md:pt-3 bg-blue-50 p-2 rounded font-semibold text-sm">
                      <span>Total Assets</span>
                      <span className="text-blue-600">{formatCurrency(assetTotal)}</span>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 md:py-6 text-gray-500 text-xs md:text-sm">
                    No assets configured
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Liabilities */}
            <Card>
              <CardHeader className="py-2 md:py-4 px-3 md:px-6">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                  <Receipt className="h-4 w-4" />
                  Liabilities
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 md:space-y-3 px-3 md:px-6">
                {groupedAccounts.liability && groupedAccounts.liability.length > 0 ? (
                  <>
                    {groupedAccounts.liability.map((account) => (
                      <div key={account.id} className="p-2 border rounded hover:bg-gray-50">
                        <div className="flex justify-between items-start mb-1.5 md:mb-2">
                          <p className="font-medium text-xs md:text-sm truncate flex-1 mr-2">{account.account_name}</p>
                          <span className="font-semibold text-xs md:text-sm flex-shrink-0">{formatCurrency(account.current_balance)}</span>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 md:h-7 text-xs px-2"
                            onClick={() => setViewTransactionsAccountId(account.id)}
                          >
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 md:h-7 text-xs px-2"
                            onClick={() => {
                              setSelectedAccount(account);
                              setManualTransactionDialogOpen(true);
                            }}
                          >
                            +Txn
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 md:h-7 text-xs px-2"
                            onClick={() => {
                              setSelectedAccount(account);
                              setEditAccountDialogOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 md:pt-3 bg-orange-50 p-2 rounded font-semibold text-sm">
                      <span>Total Liabilities</span>
                      <span className="text-orange-600">{formatCurrency(liabilityTotal)}</span>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 md:py-6 text-gray-500 text-xs md:text-sm">
                    No liabilities configured
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Equity */}
            <Card>
              <CardHeader className="py-2 md:py-4 px-3 md:px-6">
                <CardTitle className="flex items-center gap-2 text-base md:text-lg">
                  <Scale className="h-4 w-4" />
                  Equity
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 md:space-y-3 px-3 md:px-6">
                {groupedAccounts.equity && groupedAccounts.equity.length > 0 ? (
                  <>
                    {groupedAccounts.equity.map((account) => (
                      <div key={account.id} className="p-2 border rounded hover:bg-gray-50">
                        <div className="flex justify-between items-start mb-1.5 md:mb-2">
                          <p className="font-medium text-xs md:text-sm truncate flex-1 mr-2">{account.account_name}</p>
                          <span className="font-semibold text-xs md:text-sm flex-shrink-0">{formatCurrency(account.current_balance)}</span>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 md:h-7 text-xs px-2"
                            onClick={() => setViewTransactionsAccountId(account.id)}
                          >
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 md:h-7 text-xs px-2"
                            onClick={() => {
                              setSelectedAccount(account);
                              setManualTransactionDialogOpen(true);
                            }}
                          >
                            +Txn
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 md:h-7 text-xs px-2"
                            onClick={() => {
                              setSelectedAccount(account);
                              setEditAccountDialogOpen(true);
                            }}
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between pt-2 md:pt-3 bg-green-50 p-2 rounded font-semibold text-sm">
                      <span>Total Equity</span>
                      <span className="text-green-600">{formatCurrency(equityTotal)}</span>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 md:py-6 text-gray-500 text-xs md:text-sm">
                    No equity configured
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Accounting Equation Check */}
          <Card className="mt-3 md:mt-4">
            <CardHeader className="py-3 md:py-6 px-3 md:px-6">
              <CardTitle className="flex items-center gap-2 text-base md:text-xl">
                <Calculator className="h-4 w-4 md:h-5 md:w-5" />
                Balance Sheet Check
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 md:space-y-4 px-3 md:px-6">
              <div className="grid grid-cols-3 gap-2 md:gap-4">
                <div className="p-2 md:p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-xs md:text-sm text-gray-600">Assets</p>
                  <p className="text-sm md:text-2xl font-bold text-blue-600">{formatCurrency(assetTotal)}</p>
                </div>
                <div className="p-2 md:p-4 bg-orange-50 rounded-lg text-center">
                  <p className="text-xs md:text-sm text-gray-600">L + E</p>
                  <p className="text-sm md:text-2xl font-bold text-orange-600">{formatCurrency(liabilityTotal + equityTotal)}</p>
                </div>
                <div className={`p-2 md:p-4 rounded-lg text-center ${assetTotal === (liabilityTotal + equityTotal) ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className="text-xs md:text-sm text-gray-600">Diff</p>
                  <p className={`text-sm md:text-2xl font-bold ${assetTotal === (liabilityTotal + equityTotal) ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(Math.abs(assetTotal - (liabilityTotal + equityTotal)))}
                  </p>
                </div>
              </div>
              <div className={`p-2 md:p-4 rounded-lg text-center ${assetTotal === (liabilityTotal + equityTotal) ? 'bg-green-100 border border-green-400' : 'bg-red-100 border border-red-400'}`}>
                <p className={`font-semibold text-xs md:text-base ${assetTotal === (liabilityTotal + equityTotal) ? 'text-green-800' : 'text-red-800'}`}>
                  {assetTotal === (liabilityTotal + equityTotal) 
                    ? '✓ Balanced' 
                    : '⚠ Not Balanced'}
                  <span className="hidden md:inline">
                    {assetTotal === (liabilityTotal + equityTotal) 
                      ? ' (Assets = Liabilities + Equity)' 
                      : ' - discrepancy detected'}
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
            </>
          )}
          </FeatureGuard>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <AddFinancialAccountDialog 
        open={addAccountDialogOpen}
        onOpenChange={setAddAccountDialogOpen}
        onSuccess={loadFinancialData}
      />
      
      <SetOpeningBalancesDialog
        open={openingBalancesDialogOpen}
        onOpenChange={setOpeningBalancesDialogOpen}
        onSuccess={loadFinancialData}
      />
      
      {selectedAccount && (
        <>
          <EditFinancialAccountDialog
            open={editAccountDialogOpen}
            onOpenChange={setEditAccountDialogOpen}
            account={selectedAccount}
            onSuccess={loadFinancialData}
          />
          
          <ManualTransactionDialog
            open={manualTransactionDialogOpen}
            onOpenChange={setManualTransactionDialogOpen}
            accountId={selectedAccount.id}
            accountName={selectedAccount.account_name}
            onSuccess={loadFinancialData}
          />
        </>
      )}
    </div>
  );
}
